import type { TileCategory } from "@/tiles/types";

export interface CategoryTheme {
  name: string;
  badgeClass: string;
  dotClass: string;
  glowColor: string;
  tagClass: string;
  activeBorderClass: string;
  activeTextClass: string;
  activeBgClass: string;
  progressBarColor: string;
}

export const CATEGORY_THEMES: Record<TileCategory, CategoryTheme> = {
  "KI & Agents": {
    name: "KI & Agents",
    badgeClass:
      "border-purple-500/20 bg-purple-500/10 text-purple-700 dark:border-purple-400/30 dark:bg-purple-500/15 dark:text-purple-300",
    dotClass: "bg-purple-500",
    glowColor: "rgba(168, 85, 247, 0.14)",
    tagClass:
      "border-purple-500/15 bg-purple-500/5 text-purple-800/80 dark:border-purple-400/20 dark:bg-purple-500/10 dark:text-purple-300/80",
    activeBorderClass: "border-purple-500",
    activeTextClass: "text-purple-600 dark:text-purple-400 font-semibold",
    activeBgClass: "bg-purple-500/10 dark:bg-purple-500/20 text-purple-800 dark:text-purple-200",
    progressBarColor: "#a855f7",
  },
  "Web Standards & a11y": {
    name: "Web Standards & a11y",
    badgeClass:
      "border-emerald-500/20 bg-emerald-500/10 text-emerald-700 dark:border-emerald-400/30 dark:bg-emerald-500/15 dark:text-emerald-300",
    dotClass: "bg-emerald-500",
    glowColor: "rgba(160, 185, 129, 0.14)",
    tagClass:
      "border-emerald-500/15 bg-emerald-500/5 text-emerald-800/80 dark:border-emerald-400/20 dark:bg-emerald-500/10 dark:text-emerald-300/80",
    activeBorderClass: "border-emerald-500",
    activeTextClass: "text-emerald-600 dark:text-emerald-400 font-semibold",
    activeBgClass: "bg-emerald-500/10 dark:bg-emerald-500/20 text-emerald-800 dark:text-emerald-200",
    progressBarColor: "#10b981",
  },
  "Performance & Architektur": {
    name: "Performance & Architektur",
    badgeClass:
      "border-amber-500/20 bg-amber-500/10 text-amber-700 dark:border-amber-400/30 dark:bg-amber-500/15 dark:text-amber-300",
    dotClass: "bg-amber-500",
    glowColor: "rgba(245, 158, 11, 0.14)",
    tagClass:
      "border-amber-500/15 bg-amber-500/5 text-amber-800/80 dark:border-amber-400/20 dark:bg-amber-500/10 dark:text-amber-300/80",
    activeBorderClass: "border-amber-500",
    activeTextClass: "text-amber-600 dark:text-amber-400 font-semibold",
    activeBgClass: "bg-amber-500/10 dark:bg-amber-500/20 text-amber-800 dark:text-amber-200",
    progressBarColor: "#f59e0b",
  },
  "Workflows & Testing": {
    name: "Workflows & Testing",
    badgeClass:
      "border-sky-500/20 bg-sky-500/10 text-sky-700 dark:border-sky-400/30 dark:bg-sky-500/15 dark:text-sky-300",
    dotClass: "bg-sky-500",
    glowColor: "rgba(14, 165, 233, 0.14)",
    tagClass:
      "border-sky-500/15 bg-sky-500/5 text-sky-800/80 dark:border-sky-400/20 dark:bg-sky-500/10 dark:text-sky-300/80",
    activeBorderClass: "border-sky-500",
    activeTextClass: "text-sky-600 dark:text-sky-400 font-semibold",
    activeBgClass: "bg-sky-500/10 dark:bg-sky-500/20 text-sky-800 dark:text-sky-200",
    progressBarColor: "#0ea5e9",
  },
  "UI & Design Systems": {
    name: "UI & Design Systems",
    badgeClass:
      "border-rose-500/20 bg-rose-500/10 text-rose-700 dark:border-rose-400/30 dark:bg-rose-500/15 dark:text-rose-300",
    dotClass: "bg-rose-500",
    glowColor: "rgba(244, 63, 94, 0.14)",
    tagClass:
      "border-rose-500/15 bg-rose-500/5 text-rose-800/80 dark:border-rose-400/20 dark:bg-rose-500/10 dark:text-rose-300/80",
    activeBorderClass: "border-rose-500",
    activeTextClass: "text-rose-600 dark:text-rose-400 font-semibold",
    activeBgClass: "bg-rose-500/10 dark:bg-rose-500/20 text-rose-800 dark:text-rose-200",
    progressBarColor: "#f43f5e",
  },
};

const DEFAULT_THEME: CategoryTheme = {
  name: "General",
  badgeClass:
    "border-neutral-500/20 bg-neutral-500/10 text-neutral-700 dark:border-neutral-400/30 dark:bg-neutral-500/15 dark:text-neutral-300",
  dotClass: "bg-neutral-500",
  glowColor: "rgba(163, 163, 163, 0.10)",
  tagClass:
    "border-neutral-500/15 bg-neutral-500/5 text-neutral-700/80 dark:border-neutral-400/20 dark:bg-neutral-500/10 dark:text-neutral-300/80",
  activeBorderClass: "border-neutral-900 dark:border-white",
  activeTextClass: "text-neutral-900 dark:text-white font-semibold",
  activeBgClass: "bg-neutral-500/10 dark:bg-neutral-500/20 text-neutral-900 dark:text-white",
  progressBarColor: "#737373",
};

export function getCategoryTheme(category?: string): CategoryTheme {
  if (!category) return DEFAULT_THEME;
  if (category in CATEGORY_THEMES) {
    return CATEGORY_THEMES[category as TileCategory];
  }
  // Fallbacks for legacy/alternative category names
  if (category.toLowerCase().includes("ai") || category.toLowerCase().includes("ki") || category.toLowerCase().includes("behavioral")) {
    return CATEGORY_THEMES["KI & Agents"];
  }
  if (category.toLowerCase().includes("standard") || category.toLowerCase().includes("a11y") || category.toLowerCase().includes("accessibility")) {
    return CATEGORY_THEMES["Web Standards & a11y"];
  }
  if (category.toLowerCase().includes("perf") || category.toLowerCase().includes("frontend") || category.toLowerCase().includes("retrieval")) {
    return CATEGORY_THEMES["Performance & Architektur"];
  }
  if (category.toLowerCase().includes("workflow") || category.toLowerCase().includes("devops") || category.toLowerCase().includes("test") || category.toLowerCase().includes("engineer")) {
    return CATEGORY_THEMES["Workflows & Testing"];
  }
  if (category.toLowerCase().includes("component") || category.toLowerCase().includes("design") || category.toLowerCase().includes("icon") || category.toLowerCase().includes("ui")) {
    return CATEGORY_THEMES["UI & Design Systems"];
  }
  return DEFAULT_THEME;
}
